import MainLayout from "../../components/layouts/MainLayout";
import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Alert, Spin, Collapse, Card, Tag, Popconfirm, Button, message } from "antd";
import { PlusCircleOutlined, LeftCircleOutlined, CheckCircleOutlined, DeleteFilled } from "@ant-design/icons";
import { annonceApi } from "../../api/annonceApi";
import Money from "../../components/Money";

const { Panel } = Collapse;

const AnnonceDetails = () => {
    const { id } = useParams();
    const [annonce, setAnnonce] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [deletingMediaId, setDeletingMediaId] = useState(null);
    const abortController = new AbortController();

    const handleDeleteMedia = async (mediaId) => {
        setDeletingMediaId(mediaId);
        try {
            const res = await annonceApi.deleteAnnonceMedia(mediaId, abortController.signal);
            if (!res.success) {
                message.error(res.message);
                return;
            }
            message.success(res.message);
            setAnnonce((prev) => ({
                ...prev,
                medias: prev.medias.filter((media) => media.id !== mediaId),
            }));
        } catch (error) {
            message.error("Échec de la suppression de l'image");
        } finally {
            setDeletingMediaId(null);
        }
    };

    const formatDate = (date) => {
        if (!date) return "Non fourni";
        return new Date(date).toLocaleDateString("fr-FR", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const fetchAnnonce = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await annonceApi.getAnnouncement(`${id}`, abortController.signal);
            setAnnonce(res.data);
        } catch (err) {
            setError("Impossible de charger les détails de l'annonce.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnnonce();
    }, [id]);

    return (
        <MainLayout>
            <div className="container mx-auto p-6">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-semibold">Détails de l'annonce</h1>
                    <Link to={`/annonces`} className="flex items-center text-blue-600 hover:text-blue-800">
                        <LeftCircleOutlined className="mr-2" />
                        Retour
                    </Link>
                </div>

                {loading && <Spin size="large" />}
                {error && <Alert message={error} type="error" />}
                {!loading && !error && annonce && (
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        {/* Informations de base */}
                        <Collapse defaultActiveKey={["1"]} expandIconPosition="start">
                            <Panel
                                header="Informations de l'annonce"
                                key="1"
                                extra={<PlusCircleOutlined />}
                            >
                                <div className="space-y-4">
                                    <p>
                                        <strong>Titre de l'annonce:</strong> {annonce.title}
                                    </p>
                                    <p>
                                        <strong>Description :</strong>{" "}
                                        {annonce?.description || "Non fourni"}
                                    </p>
                                    <p>
                                        <strong>Pays :</strong> {annonce?.country || "Non fourni"}
                                    </p>
                                    <p>
                                        <strong>Adresse :</strong> {annonce?.city || "Non fourni"}
                                    </p>
                                    <p>
                                        <strong>Catégorie :</strong>{" "}
                                        {annonce?.category?.libelle || "Non fourni"}
                                    </p>
                                    <p>
                                        <strong>Prix :</strong> {annonce?.price ? <Money amount={annonce.price} /> : "Non fourni"}
                                    </p>
                                    <p>
                                        <strong>Date publication :</strong>{" "}
                                        {formatDate(annonce?.created_at)}
                                    </p>
                                    <p>
                                        <strong>Date expiration :</strong>{" "}
                                        {formatDate(annonce?.expired_at)}
                                    </p>
                                    <p>
                                        <strong>Vues :</strong> {annonce?.views || 0}
                                    </p>
                                    <p>
                                        <strong>Likes :</strong> {annonce?.likes || 0}
                                    </p>
                                    <p>
                                        <strong>Signaler comme abus :</strong> {annonce?.abus || 0}
                                    </p>
                                    <p>
                                        <strong>Annonce mise en avant :</strong>{" "}
                                        {annonce?.highlighting === 1 ? "OUI" : "NON"}
                                    </p>
                                    <p>
                                        <strong>Contact WhatsApp :</strong>{" "}
                                        {annonce?.whatsapp || "Non fourni"}
                                    </p>
                                    <p>
                                        <strong>Formation avec quiz :</strong>{" "}
                                        {annonce?.is_training === 1 ? (
                                            <Tag color="green">OUI</Tag>
                                        ) : (
                                            <Tag color="default">NON</Tag>
                                        )}
                                    </p>
                                </div>
                            </Panel>
                        </Collapse>

                        {/* Quiz Section */}
                        {annonce?.is_training === 1 && (
                            <Collapse
                                defaultActiveKey={["2"]}
                                expandIconPosition="start"
                                className="mt-4"
                            >
                                <Panel
                                header="Quiz associé"
                                key="2"
                                extra={<PlusCircleOutlined />}
                                >
                                {annonce?.quiz?.questions?.length > 0 ? (
                                    <div className="space-y-6">
                                    {annonce.quiz.questions.map((quizItem, questionIndex) => (
                                        <Card
                                        key={questionIndex}
                                        title={`Question ${questionIndex + 1}`}
                                        className="mb-4"
                                        >
                                        <p className="text-lg font-semibold mb-4">
                                            {quizItem.libelle}
                                        </p>
                                        <div className="space-y-2">
                                            {quizItem.answers &&
                                            quizItem.answers.map((answer, answerIndex) => (
                                                <div
                                                key={answerIndex}
                                                className={`p-3 rounded-md border-2 ${
                                                    answer.is_true === 1
                                                    ? "bg-green-50 border-green-500"
                                                    : "bg-gray-50 border-gray-200"
                                                }`}
                                                >
                                                <div className="flex items-center justify-between">
                                                    <span
                                                    className={
                                                        answer.is_true === 1
                                                        ? "font-semibold text-green-700"
                                                        : "text-gray-700"
                                                    }
                                                    >
                                                    {answer.libelle}
                                                    </span>
                                                    {answer.is_true === 1 && (
                                                    <Tag
                                                        color="success"
                                                        icon={<CheckCircleOutlined />}
                                                    >
                                                        Bonne réponse
                                                    </Tag>
                                                    )}
                                                </div>
                                                </div>
                                            ))}
                                        </div>
                                        </Card>
                                    ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 italic">
                                    Aucun quiz associé à cette annonce.
                                    </p>
                                )}
                                </Panel>
                            </Collapse>
                            )}


                        {/* Auteur */}
                        {annonce?.user && (
                            <Collapse
                                defaultActiveKey={["3"]}
                                expandIconPosition="start"
                                className="mt-4"
                            >
                                <Panel
                                    header="Auteur de la publication"
                                    key="3"
                                    extra={<PlusCircleOutlined />}
                                >
                                    <div className="space-y-4">
                                        <p>
                                            <strong>Nom complet :</strong>{" "}
                                            {annonce?.user?.name} {annonce?.user?.lastname}
                                        </p>
                                        <p>
                                            <strong>Contact :</strong> {annonce?.user?.contact || "Non fourni"}
                                        </p>
                                    </div>
                                </Panel>
                            </Collapse>
                        )}

                        {/* Images */}
                        {annonce?.medias && annonce.medias.filter((img) => img?.etat === 1).length > 0 && (
                            <Collapse
                                defaultActiveKey={["4"]}
                                expandIconPosition="start"
                                className="mt-4"
                            >
                                <Panel header="Images" key="4" extra={<PlusCircleOutlined />}>
                                    <div className="flex flex-wrap gap-4">
                                        {annonce.medias
                                            .filter((image) => image?.etat === 1)
                                            .map((image, index) => (
                                                <div
                                                    key={index}
                                                    className="relative w-32 h-32 border border-gray-300 rounded-md overflow-hidden"
                                                >
                                                    <img
                                                        src={image?.file_path}
                                                        alt={annonce?.title || "Image"}
                                                        className="w-full h-full object-cover"
                                                    />
                                                    <Popconfirm
                                                        title="Supprimer cette image ?"
                                                        onConfirm={() => handleDeleteMedia(image.id)}
                                                        okText="Oui"
                                                        cancelText="Non"
                                                    >
                                                        <Button
                                                            danger
                                                            size="small"
                                                            shape="circle"
                                                            icon={<DeleteFilled />}
                                                            loading={deletingMediaId === image.id}
                                                            className="absolute top-1 right-1"
                                                        />
                                                    </Popconfirm>
                                                </div>
                                            ))}
                                    </div>
                                </Panel>
                            </Collapse>
                        )}
                    </div>
                )}
            </div>
        </MainLayout>
    );
};

export default AnnonceDetails;
